import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Chip, Skeleton, Stack, Typography } from '@mui/material';
import Card from '../common/Card';
import StatusBadge from './StatusBadge';

const formatValue = (value) => (value ? new Date(value).toLocaleString() : 'N/A');

const LearningHeader = ({
  symbol,
  scheduler,
  learningThreshold,
  profileVersion,
  lastEvaluatedAt,
  lastActivatedAt,
  lastRunStatus,
  lastRunReasonCode,
  lastRunReasonMeta,
  loading = false,
}) => {
  const statusColor = scheduler?.enabled ? 'success' : 'default';
  const readyColor = learningThreshold?.readyToLearn ? 'success' : 'warning';

  return (
    <Card title={`${symbol} Learning`} subheader="Single-symbol learning overview">
      {loading ? (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={32} />
          <Skeleton variant="rounded" height={24} />
          <Skeleton variant="rounded" height={42} />
        </Stack>
      ) : (
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
          <Chip label={`Scheduler: ${scheduler?.enabled ? 'Enabled' : 'Disabled'}`} color={statusColor} />
          <Chip label={`Cron: ${scheduler?.cron || 'N/A'}`} variant="outlined" />
          <Chip label={`Closed trades: ${learningThreshold?.currentClosedTrades || 0}/${learningThreshold?.requiredClosedTrades || 0}`} color={readyColor} />
          <Chip label={`Profile v${profileVersion || 1}`} variant="outlined" />
          <StatusBadge value={lastRunStatus || 'not_applicable'} variant="outlined" />
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
          <Typography variant="body2" color="text.secondary">
            Last evaluated: {formatValue(lastEvaluatedAt)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last activated: {formatValue(lastActivatedAt)}
          </Typography>
        </Stack>

        {lastRunReasonCode && (
          <Alert severity={lastRunStatus === 'failed' ? 'error' : 'info'}>
            Reason: {lastRunReasonCode}
            {lastRunReasonMeta?.current !== undefined ? ` (${lastRunReasonMeta.current})` : ''}
          </Alert>
        )}
      </Stack>
      )}
    </Card>
  );
};

LearningHeader.propTypes = {
  symbol: PropTypes.string.isRequired,
  scheduler: PropTypes.shape({
    enabled: PropTypes.bool,
    cron: PropTypes.string,
  }),
  learningThreshold: PropTypes.shape({
    requiredClosedTrades: PropTypes.number,
    currentClosedTrades: PropTypes.number,
    readyToLearn: PropTypes.bool,
  }),
  profileVersion: PropTypes.number,
  lastEvaluatedAt: PropTypes.string,
  lastActivatedAt: PropTypes.string,
  lastRunStatus: PropTypes.string,
  lastRunReasonCode: PropTypes.string,
  lastRunReasonMeta: PropTypes.object,
  loading: PropTypes.bool,
};

export default LearningHeader;
